import { useEffect, useState, useRef, useCallback } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, Minus, Copy, Search, Download, Wand2, Undo2, Crown, Circle, RectangleHorizontal,
  X, ZoomIn, ZoomOut, Maximize, Crosshair, Users, Trash2, MoveRight, Pencil, Eraser, RotateCw,
} from "lucide-react";

const RECT = (s) => s === "rectangle" || s === "banquet";
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// seat positions relative to the table box top-left (world units)
function seatPositions(t) {
  const cap = Math.max(1, t.capacity || 1);
  const W = t.width || 120;
  const H = RECT(t.shape) ? (t.height || 90) : W;
  const pos = [];
  if (RECT(t.shape)) {
    const nTop = Math.ceil(cap / 2);
    const nBottom = cap - nTop;
    for (let i = 0; i < nTop; i++) pos.push({ x: (W * (i + 1)) / (nTop + 1), y: -18 });
    for (let i = 0; i < nBottom; i++) pos.push({ x: (W * (i + 1)) / (nBottom + 1), y: H + 18 });
  } else {
    const cx = W / 2, cy = H / 2, R = W / 2 + 18;
    for (let i = 0; i < cap; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / cap;
      pos.push({ x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) });
    }
  }
  return pos;
}

export default function Seating() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [data, setData] = useState(null);
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");
  const [selectedGuest, setSelectedGuest] = useState(null); // guest being placed/moved
  const [seatMenu, setSeatMenu] = useState(null); // {guest, table, seat}
  const [showPanel, setShowPanel] = useState(false);
  const [view, setView] = useState({ scale: 1, tx: 40, ty: 40 });
  const [history, setHistory] = useState([]);
  const [drawMode, setDrawMode] = useState(false);
  const [eraseMode, setEraseMode] = useState(false);
  const [drawColor, setDrawColor] = useState("#9a8f7d");
  const [drawWidth, setDrawWidth] = useState(4);
  const [strokes, setStrokes] = useState([]);
  const [cur, setCur] = useState(null);
  const strokesRef = useRef([]);
  const setStrokesBoth = (s) => { strokesRef.current = s; setStrokes(s); };

  const vp = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const fitted = useRef(false);

  const load = useCallback(async (fit = false) => {
    const { data: d } = await api.get(`/weddings/${wid}/seating`);
    setData(d);
    const g = await api.get(`/weddings/${wid}/guests`);
    setGuests(g.data.filter((x) => x.rsvp_status === "attending"));
    if (fit) fitted.current = false;
  }, [wid]);
  useEffect(() => { if (wid) load(true); }, [wid, load]);

  useEffect(() => {
    const d = data?.layout?.drawing || [];
    strokesRef.current = d; setStrokes(d);
  }, [data?.layout?.id]);

  const persistDrawing = (s) => api.patch(`/weddings/${wid}/seating/layout`, { drawing: s }).catch(() => {});
  const eraseAt = (w) => setStrokesBoth(strokesRef.current.filter((st) => !st.points.some((pt) => Math.hypot(pt.x - w.x, pt.y - w.y) < 16)));
  const undoDraw = () => { const s = strokesRef.current.slice(0, -1); setStrokesBoth(s); persistDrawing(s); };
  const rotateTable = async (t) => { const next = ((Math.round((t.rotation || 0) / 45) * 45) + 45) % 360; await api.patch(`/weddings/${wid}/seating/tables/${t.id}`, { rotation: next }); load(); };

  const fitAll = useCallback((tables) => {
    const el = vp.current; if (!el) return;
    const vw = el.clientWidth, vh = el.clientHeight;
    const ts = tables || data?.tables || [];
    if (!ts.length) { setView({ scale: 1, tx: vw / 2 - 60, ty: vh / 2 - 60 }); return; }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ts.forEach((t) => {
      const W = t.width || 120, H = RECT(t.shape) ? (t.height || 90) : W;
      minX = Math.min(minX, t.x - 40); minY = Math.min(minY, t.y - 40);
      maxX = Math.max(maxX, t.x + W + 40); maxY = Math.max(maxY, t.y + H + 40);
    });
    const cw = maxX - minX, ch = maxY - minY;
    const scale = clamp(Math.min(vw / cw, vh / ch), 0.25, 1.4);
    setView({ scale, tx: (vw - cw * scale) / 2 - minX * scale, ty: (vh - ch * scale) / 2 - minY * scale });
  }, [data]);

  // fit once when tables first arrive
  useEffect(() => {
    if (data && !fitted.current && vp.current) { fitted.current = true; setTimeout(() => fitAll(data.tables), 60); }
  }, [data, fitAll]);

  if (!data) return <Loader />;

  const assignedIds = new Set(data.assignments.map((a) => a.guest_id));
  const unseated = guests.filter((g) => !assignedIds.has(g.id) &&
    `${g.first_name} ${g.last_name}`.toLowerCase().includes(q.toLowerCase()));
  const guestById = (id) => guests.find((g) => g.id === id);
  const assignsAt = (tid) => data.assignments.filter((a) => a.table_id === tid);
  const occupantOf = (tid, i) => {
    const a = data.assignments.find((x) => x.table_id === tid && x.seat_index === i);
    return a ? guestById(a.guest_id) : null;
  };

  const snapshot = () => setHistory((h) => [...h.slice(-19), JSON.stringify(data.assignments)]);

  // ---------- table CRUD ----------
  const addTable = async (shape) => {
    const n = data.tables.length + 1;
    // place new table near current viewport centre (world coords)
    const el = vp.current;
    const wx = el ? (el.clientWidth / 2 - view.tx) / view.scale - 60 : 100;
    const wy = el ? (el.clientHeight / 2 - view.ty) / view.scale - 60 : 100;
    await api.post(`/weddings/${wid}/seating/tables`, {
      name: `Table ${n}`, shape, capacity: shape === "sweetheart" ? 2 : 8,
      x: Math.round(wx + (n % 3) * 30), y: Math.round(wy + (n % 3) * 30),
      width: RECT(shape) ? 180 : 120, height: RECT(shape) ? 90 : 120,
    }).catch((e) => toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erreur"));
    load();
  };
  const delTable = async (tid) => { await api.delete(`/weddings/${wid}/seating/tables/${tid}`); load(); };
  const duplicate = async (tid) => { await api.post(`/weddings/${wid}/seating/tables/${tid}/duplicate`).catch((e) => toast.error(e.response?.data?.detail?.message || "Limite atteinte")); load(); };

  const changeCapacity = async (t, newCap) => {
    newCap = clamp(newCap, 1, 30);
    if (newCap === t.capacity) return;
    try { await api.patch(`/weddings/${wid}/seating/tables/${t.id}`, { capacity: newCap }); load(); }
    catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === "CAPACITY_CONFLICT") {
        if (window.confirm(`${det.message}\n\nRéduire quand même ?`)) {
          await api.patch(`/weddings/${wid}/seating/tables/${t.id}`, { capacity: newCap, force: true }); load();
        }
      } else toast.error(det?.message || "Erreur");
    }
  };

  // ---------- assignment ----------
  const assignToSeat = async (gid, tid, seat) => {
    snapshot();
    try { await api.post(`/weddings/${wid}/seating/assign`, { guest_id: gid, table_id: tid, seat_index: seat }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Placement impossible"); }
    setSelectedGuest(null); setSeatMenu(null);
  };
  const assignToTable = (gid, tid) => assignToSeat(gid, tid, null);
  const unassign = async (gid) => { snapshot(); await api.post(`/weddings/${wid}/seating/unassign`, { guest_id: gid }); setSeatMenu(null); load(); };

  const undo = async () => {
    if (!history.length) return;
    const prev = JSON.parse(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
    for (const g of guests) await api.post(`/weddings/${wid}/seating/unassign`, { guest_id: g.id });
    for (const a of prev) await api.post(`/weddings/${wid}/seating/assign`, { guest_id: a.guest_id, table_id: a.table_id, seat_index: a.seat_index });
    load();
  };

  const autoDistribute = async () => {
    const { data: prop } = await api.post(`/weddings/${wid}/seating/auto`);
    if (prop.unseated_count === 0) { toast.info("Tous les invités confirmés sont déjà placés."); return; }
    if (window.confirm(`Proposer un placement pour ${prop.unseated_count} invités ?`)) {
      snapshot(); await api.post(`/weddings/${wid}/seating/auto/apply`, { proposal: prop.proposal });
      toast.success("Répartition appliquée"); load();
    }
  };

  // ---------- pan / zoom / drag (unified pointer events) ----------
  const localPt = (e) => { const r = vp.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };

  const onPointerDown = (e) => {
    // Seats and toolbar buttons must receive their native click — do NOT capture the pointer
    // on the viewport for them (capturing would swallow the click and break placement/zoom).
    if (e.target.closest("[data-seat]") || e.target.closest("button") || e.target.closest("[data-noselect]")) return;
    const p = localPt(e);
    if (drawMode || eraseMode) {
      pointers.current.set(e.pointerId, p);
      vp.current.setPointerCapture?.(e.pointerId);
      const w = { x: (p.x - view.tx) / view.scale, y: (p.y - view.ty) / view.scale };
      if (eraseMode) { gesture.current = { type: "erase" }; eraseAt(w); return; }
      const st = { color: drawColor, width: drawWidth, points: [w] };
      gesture.current = { type: "draw", stroke: st }; setCur({ ...st }); return;
    }
    pointers.current.set(e.pointerId, p);
    vp.current.setPointerCapture?.(e.pointerId);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2, midY = (a.y + b.y) / 2;
      gesture.current = { type: "pinch", dist, scale: view.scale,
        worldX: (midX - view.tx) / view.scale, worldY: (midY - view.ty) / view.scale };
      return;
    }
    const hit = e.target.closest("[data-table-hit]");
    if (hit) {
      const t = data.tables.find((x) => x.id === hit.getAttribute("data-table-hit"));
      gesture.current = { type: "table", id: t.id, startX: t.x, startY: t.y, px: p.x, py: p.y, moved: false };
    } else {
      gesture.current = { type: "pan", tx: view.tx, ty: view.ty, px: p.x, py: p.y };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const p = localPt(e);
    pointers.current.set(e.pointerId, p);
    const g = gesture.current;
    if (!g) return;
    if (g.type === "draw") { const w = { x: (p.x - view.tx) / view.scale, y: (p.y - view.ty) / view.scale }; g.stroke.points.push(w); setCur({ color: g.stroke.color, width: g.stroke.width, points: [...g.stroke.points] }); return; }
    if (g.type === "erase") { const w = { x: (p.x - view.tx) / view.scale, y: (p.y - view.ty) / view.scale }; eraseAt(w); return; }
    if (g.type === "pinch" && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const midX = (a.x + b.x) / 2, midY = (a.y + b.y) / 2;
      const scale = clamp(g.scale * (dist / g.dist), 0.25, 3);
      setView({ scale, tx: midX - g.worldX * scale, ty: midY - g.worldY * scale });
    } else if (g.type === "table") {
      const dx = (p.x - g.px) / view.scale, dy = (p.y - g.py) / view.scale;
      if (Math.abs(p.x - g.px) + Math.abs(p.y - g.py) > 3) g.moved = true;
      const nx = Math.round(g.startX + dx), ny = Math.round(g.startY + dy);
      setData((d) => ({ ...d, tables: d.tables.map((t) => t.id === g.id ? { ...t, x: nx, y: ny } : t) }));
    } else if (g.type === "pan") {
      setView((v) => ({ ...v, tx: g.tx + (p.x - g.px), ty: g.ty + (p.y - g.py) }));
    }
  };

  const onPointerUp = async (e) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g?.type === "draw") { const s = [...strokesRef.current, g.stroke]; setStrokesBoth(s); persistDrawing(s); setCur(null); gesture.current = null; return; }
    if (g?.type === "erase") { persistDrawing(strokesRef.current); gesture.current = null; return; }
    if (g?.type === "table") {
      if (g.moved) {
        const t = data.tables.find((x) => x.id === g.id);
        if (t) await api.patch(`/weddings/${wid}/seating/tables/${t.id}`, { x: t.x, y: t.y });
      } else if (selectedGuest) {
        assignToTable(selectedGuest.id, g.id); // tap on table body = seat at first free spot
      }
    }
    if (pointers.current.size <= 1) gesture.current = null;
  };

  const zoomBy = (f) => {
    const el = vp.current; const cx = el.clientWidth / 2, cy = el.clientHeight / 2;
    setView((v) => {
      const ns = clamp(v.scale * f, 0.25, 3);
      const wx = (cx - v.tx) / v.scale, wy = (cy - v.ty) / v.scale;
      return { scale: ns, tx: cx - wx * ns, ty: cy - wy * ns };
    });
  };
  const recenter = () => {
    const el = vp.current; const ts = data.tables || [];
    if (!ts.length) { setView({ scale: 1, tx: el.clientWidth / 2, ty: el.clientHeight / 2 }); return; }
    const cx = ts.reduce((s, t) => s + t.x, 0) / ts.length, cy = ts.reduce((s, t) => s + t.y, 0) / ts.length;
    setView({ scale: 1, tx: el.clientWidth / 2 - cx, ty: el.clientHeight / 2 - cy });
  };

  const onSeatClick = (t, i) => {
    const occ = occupantOf(t.id, i);
    if (selectedGuest) { assignToSeat(selectedGuest.id, t.id, i); return; }
    if (occ) setSeatMenu({ guest: occ, table: t, seat: i });
  };

  // ---------- guest panel content (shared desktop + mobile) ----------
  const Panel = () => (
    <div className="space-y-4">
      <div>
        <div className="relative mb-2"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un invité…" className="pl-8 h-9" data-testid="guest-search" /></div>
        <div className="text-xs uppercase tracking-wide text-stone-400 mb-2">À placer ({unseated.length})</div>
        <div className="space-y-1.5 max-h-[38vh] lg:max-h-[420px] overflow-y-auto">
          {unseated.map((g) => (
            <button key={g.id} onClick={() => { setSelectedGuest(selectedGuest?.id === g.id ? null : g); setShowPanel(false); }}
              data-testid={`unseated-${g.id}`}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${selectedGuest?.id === g.id ? "bg-stone-900 text-white border-stone-900" : "bg-white hover:bg-accent/40"}`}>
              {g.vip && <Crown className="w-3 h-3 text-champagne" />}
              <span className="flex-1 truncate">{g.first_name} {g.last_name}</span>
              {g.dietary && <span className="w-2 h-2 rounded-full bg-sage" title={g.dietary} />}
            </button>
          ))}
          {unseated.length === 0 && <p className="text-xs text-stone-400 py-4 text-center">Tous placés 🎉</p>}
        </div>
      </div>
      <div className="border-t pt-3">
        <div className="text-xs uppercase tracking-wide text-stone-400 mb-2">Ajouter une table</div>
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" variant="outline" onClick={() => addTable("round")} data-testid="add-round"><Circle className="w-4 h-4 mr-1" /> Ronde</Button>
          <Button size="sm" variant="outline" onClick={() => addTable("rectangle")} data-testid="add-rect"><RectangleHorizontal className="w-4 h-4 mr-1" /> Rectangle</Button>
          <Button size="sm" variant="outline" onClick={() => addTable("banquet")}>Banquet</Button>
          <Button size="sm" variant="outline" onClick={() => addTable("sweetheart")}>Mariés (2)</Button>
        </div>
      </div>
      {data.tables.length > 0 && (
        <div className="border-t pt-3">
          <div className="text-xs uppercase tracking-wide text-stone-400 mb-2">Tables & capacité</div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {data.tables.map((t) => {
              const n = assignsAt(t.id).length;
              return (
                <div key={t.id} className="flex items-center gap-1.5 text-xs" data-testid={`table-edit-${t.id}`}>
                  <span className="flex-1 truncate">{t.name} <span className="text-stone-400">· {n}/{t.capacity}</span></span>
                  <button onClick={() => changeCapacity(t, t.capacity - 1)} className="w-7 h-7 rounded border flex items-center justify-center" data-testid={`cap-minus-${t.id}`}><Minus className="w-3 h-3" /></button>
                  <input type="number" value={t.capacity} onChange={(e) => changeCapacity(t, parseInt(e.target.value || "1"))} className="w-11 h-7 text-center rounded border" data-testid={`cap-input-${t.id}`} />
                  <button onClick={() => changeCapacity(t, t.capacity + 1)} className="w-7 h-7 rounded border flex items-center justify-center" data-testid={`cap-plus-${t.id}`}><Plus className="w-3 h-3" /></button>
                  <button onClick={() => rotateTable(t)} className="text-stone-400 hover:text-stone-800 px-1" data-testid={`rotate-${t.id}`} title="Pivoter 45°"><RotateCw className="w-3.5 h-3.5" /></button>
                  <button onClick={() => duplicate(t.id)} className="text-stone-400 hover:text-stone-800 px-1" data-testid={`dup-${t.id}`}><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => delTable(t.id)} className="text-stone-300 hover:text-destructive px-1" data-testid={`del-table-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader eyebrow="Plan de table" title="Plan de salle visuel"
        description={`${data.assignments.length}/${guests.length} invités placés · placez chacun à son siège`}
        actions={<>
          <Button variant="outline" size="icon" onClick={undo} disabled={!history.length} data-testid="undo-btn"><Undo2 className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={autoDistribute} data-testid="auto-seat"><Wand2 className="w-4 h-4 mr-1" /> Auto</Button>
          <Button variant="outline" onClick={() => window.open(`${API}/weddings/${wid}/seating/catering-export`, "_blank")}><Download className="w-4 h-4 mr-1" /> Traiteur</Button>
        </>} />

      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <div className="hidden lg:block card-lux p-4 h-fit lg:sticky lg:top-6"><Panel /></div>

        {/* Canvas / viewport */}
        <div className="relative">
          <div ref={vp} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
            className="card-lux relative overflow-hidden bg-[#F4F0EA] h-[68vh] min-h-[460px] select-none"
            style={{ touchAction: "none", backgroundImage: "radial-gradient(#e0d9cc 1px, transparent 1px)", backgroundSize: "24px 24px" }}
            data-testid="seating-canvas">

            {data.tables.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-400 pointer-events-none">
                <Users className="w-10 h-10 mb-3" />
                <p className="text-sm px-6 text-center">Ajoutez vos tables, puis placez chaque invité à son siège.</p>
              </div>
            )}

            {/* world */}
            <div className="absolute top-0 left-0 origin-top-left" style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})` }}>
              <svg className="absolute top-0 left-0 pointer-events-none" style={{ overflow: "visible" }} width="1" height="1" data-testid="drawing-layer">
                {[...strokes, cur].filter(Boolean).map((st, i) => (
                  <polyline key={i} fill="none" stroke={st.color} strokeWidth={st.width} strokeLinecap="round" strokeLinejoin="round"
                    points={st.points.map((p) => `${p.x},${p.y}`).join(" ")} />
                ))}
              </svg>
              {data.tables.map((t) => {
                const W = t.width || 120, H = RECT(t.shape) ? (t.height || 90) : W;
                const seats = seatPositions(t);
                const n = assignsAt(t.id).length;
                const full = n >= t.capacity;
                return (
                  <div key={t.id} className="absolute" style={{ left: t.x, top: t.y, width: W, height: H, transform: `rotate(${t.rotation || 0}deg)` }}>
                    {/* table body (drag handle) */}
                    <div data-table-hit={t.id} data-testid={`table-${t.id}`}
                      className={`absolute inset-0 flex flex-col items-center justify-center border-2 cursor-move ${RECT(t.shape) ? "rounded-xl" : "rounded-full"} ${full ? "border-champagne bg-champagne/15" : "border-sage bg-sage/15"} ${selectedGuest ? "ring-2 ring-stone-900 ring-offset-2" : ""}`}>
                      <div className="font-medium text-[11px] text-stone-800 text-center px-1 leading-tight">{t.name}</div>
                      <div className={`text-[9px] ${full ? "text-champagne font-semibold" : "text-stone-500"}`}>{full ? "Complète" : `${n}/${t.capacity}`}</div>
                    </div>

                    {/* seats */}
                    {seats.map((s, i) => {
                      const occ = occupantOf(t.id, i);
                      return (
                        <div key={i} data-seat data-testid={`seat-${t.id}-${i}`} onClick={() => onSeatClick(t, i)}
                          className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center cursor-pointer"
                          style={{ left: s.x, top: s.y }}>
                          <div title={occ ? `${occ.first_name} ${occ.last_name}` : `Siège ${i + 1}`}
                            className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-semibold shadow-sm ${occ ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-400 border-dashed border-stone-300 hover:border-stone-500"} ${selectedGuest ? "ring-1 ring-stone-900" : ""}`}>
                            {occ ? (occ.first_name?.[0] || "?").toUpperCase() : i + 1}
                          </div>
                          {occ && <span className="mt-0.5 max-w-[52px] truncate text-[8px] leading-none text-stone-700 bg-white/80 rounded px-1">{occ.first_name}</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* draw toolbar (venue sketch layer) */}
            <div data-noselect className="absolute top-3 left-3 flex items-center gap-1 bg-white/95 rounded-lg shadow border p-1">
              <button onClick={() => { setDrawMode((m) => !m); setEraseMode(false); }} data-testid="draw-toggle" className={`w-8 h-8 rounded flex items-center justify-center ${drawMode ? "bg-stone-900 text-white" : "hover:bg-accent/50"}`} title="Dessiner la salle"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => { setEraseMode((m) => !m); setDrawMode(false); }} data-testid="erase-toggle" className={`w-8 h-8 rounded flex items-center justify-center ${eraseMode ? "bg-stone-900 text-white" : "hover:bg-accent/50"}`} title="Effacer"><Eraser className="w-4 h-4" /></button>
              <button onClick={undoDraw} data-testid="draw-undo" className="w-8 h-8 rounded flex items-center justify-center hover:bg-accent/50" title="Annuler le dernier trait"><Undo2 className="w-4 h-4" /></button>
              {drawMode && <input type="color" value={drawColor} onChange={(e) => setDrawColor(e.target.value)} data-testid="draw-color" className="w-8 h-8 rounded border p-0.5 cursor-pointer" />}
              {drawMode && <select value={drawWidth} onChange={(e) => setDrawWidth(+e.target.value)} data-testid="draw-width" className="h-8 rounded border text-xs px-1"><option value={2}>Fin</option><option value={4}>Moyen</option><option value={8}>Épais</option></select>}
            </div>

            {/* zoom toolbar */}
            <div className="absolute top-3 right-3 flex flex-col gap-1.5">
              <button onClick={() => zoomBy(1.2)} data-testid="zoom-in" className="w-9 h-9 rounded-lg bg-white shadow border flex items-center justify-center"><ZoomIn className="w-4 h-4" /></button>
              <button onClick={() => zoomBy(1 / 1.2)} data-testid="zoom-out" className="w-9 h-9 rounded-lg bg-white shadow border flex items-center justify-center"><ZoomOut className="w-4 h-4" /></button>
              <button onClick={recenter} data-testid="recenter-btn" className="w-9 h-9 rounded-lg bg-white shadow border flex items-center justify-center" title="Recentrer"><Crosshair className="w-4 h-4" /></button>
              <button onClick={() => fitAll()} data-testid="fit-all-btn" className="w-9 h-9 rounded-lg bg-white shadow border flex items-center justify-center" title="Tout afficher"><Maximize className="w-4 h-4" /></button>
            </div>

            {/* placing banner */}
            {selectedGuest && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-stone-900 text-white text-xs px-4 py-2 rounded-full shadow-lg flex items-center gap-2" data-testid="placing-banner">
                Touchez un siège pour placer <b>{selectedGuest.first_name}</b>
                <button onClick={() => setSelectedGuest(null)}><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>

          {/* mobile: open guests panel */}
          <div className="lg:hidden mt-3">
            <Button className="w-full rounded-full" onClick={() => setShowPanel(true)} data-testid="guest-panel-toggle">
              <Users className="w-4 h-4 mr-2" /> Invités & tables ({unseated.length} à placer)
            </Button>
          </div>
        </div>
      </div>

      {/* mobile drawer */}
      {showPanel && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setShowPanel(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="guest-drawer">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Invités & tables</div>
              <button onClick={() => setShowPanel(false)}><X className="w-5 h-5" /></button>
            </div>
            <Panel />
          </div>
        </div>
      )}

      {/* seat action menu */}
      {seatMenu && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => setSeatMenu(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-xl p-4 w-full sm:w-80 m-0 sm:m-4 rounded-b-none sm:rounded-b-2xl" onClick={(e) => e.stopPropagation()} data-testid="seat-menu">
            <div className="text-sm font-semibold mb-1">{seatMenu.guest.first_name} {seatMenu.guest.last_name}</div>
            <div className="text-xs text-stone-400 mb-3">{seatMenu.table.name} · siège {seatMenu.seat + 1}</div>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" data-testid="seat-move" onClick={() => { setSelectedGuest(seatMenu.guest); setSeatMenu(null); }}>
                <MoveRight className="w-4 h-4 mr-2" /> Déplacer / changer de place
              </Button>
              <Button variant="outline" className="w-full justify-start text-destructive" data-testid="seat-remove" onClick={() => unassign(seatMenu.guest.id)}>
                <X className="w-4 h-4 mr-2" /> Retirer de la table
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
